import React, { useEffect, useState } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS, SITE } from "@/constants/site";
import { NAV } from "@/constants/testIds";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  // Home page opens with a dark hero — nav text must read as cream over it
  // when not scrolled. All other public pages open on a cream background.
  const darkHero = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock scroll when overlay open
  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [open]);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  if (isAdmin) {
    // Minimal nav for admin
    return (
      <header
        data-testid={NAV.root}
        className="fixed top-0 left-0 right-0 z-40"
        style={{
          backgroundColor: "var(--rk-dark-95)",
          color: "var(--rk-cream)",
          borderBottom: "1px solid var(--rk-copper)",
        }}
      >
        <div className="rk-container-wide flex items-center justify-between h-16">
          <Link
            to="/"
            data-testid={NAV.brand}
            style={{ color: "inherit", textDecoration: "none" }}
            className="text-lg"
          >
            <span style={{ fontFamily: "var(--font-heading)", fontSize: 22, letterSpacing: "-0.01em" }}>
              Rakia Finley
            </span>
          </Link>
          <span className="rk-caption" style={{ color: "var(--rk-cream)", opacity: 0.7 }}>
            Admin
          </span>
        </div>
      </header>
    );
  }

  return (
    <>
      <header
        data-testid={NAV.root}
        className="fixed top-0 left-0 right-0 z-40"
        style={{
          backgroundColor: scrolled ? "var(--rk-dark-95)" : "transparent",
          color: scrolled
            ? "var(--rk-cream)"
            : darkHero
              ? "var(--rk-cream)"
              : "var(--rk-ink)",
          borderBottom: scrolled
            ? "1px solid var(--rk-copper)"
            : "1px solid transparent",
          transition:
            "background-color 200ms ease, color 200ms ease, border-color 200ms ease",
          backdropFilter: scrolled ? "saturate(140%) blur(6px)" : "none",
        }}
      >
        <div className="rk-container-wide flex items-center justify-between h-[72px] md:h-[84px]">
          <Link
            to="/"
            data-testid={NAV.brand}
            style={{ color: "inherit", textDecoration: "none" }}
            aria-label="Rakia Finley — home"
          >
            <span
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(22px, 2.2vw, 28px)",
                letterSpacing: "-0.01em",
              }}
            >
              Rakia Finley
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.to === "/"}
                data-testid={NAV[`link${l.key.charAt(0).toUpperCase() + l.key.slice(1)}`]}
                style={({ isActive }) => ({
                  color: "inherit",
                  fontFamily: "var(--font-body)",
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 500,
                  textDecoration: "none",
                  position: "relative",
                  paddingBottom: 6,
                  borderBottom: isActive
                    ? "1px solid var(--rk-copper)"
                    : "1px solid transparent",
                  transition: "border-color 150ms ease, color 150ms ease",
                })}
                className="hover:[border-bottom-color:var(--rk-copper)]"
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/advisory"
              data-testid={NAV.ctaWork}
              className="rk-btn-primary"
              style={{ height: 40, padding: "0 18px", fontSize: 14 }}
            >
              Work with Rakia
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="md:hidden"
            data-testid={NAV.mobileOpen}
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            style={{
              background: "transparent",
              border: "1px solid currentColor",
              padding: 10,
              borderRadius: 2,
              color: "inherit",
            }}
          >
            <Menu size={20} />
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileOverlay}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 60,
              backgroundColor: "var(--rk-dark)",
              color: "var(--rk-cream)",
              display: "flex",
              flexDirection: "column",
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
          >
            <div className="flex items-center justify-between rk-container-wide h-[72px]">
              <span
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: 24,
                  letterSpacing: "-0.01em",
                }}
              >
                Rakia Finley
              </span>
              <button
                type="button"
                data-testid={NAV.mobileClose}
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                style={{
                  background: "transparent",
                  border: "1px solid var(--rk-copper)",
                  color: "var(--rk-copper)",
                  padding: 10,
                  borderRadius: 2,
                }}
              >
                <X size={20} />
              </button>
            </div>
            <motion.nav
              className="flex-1 rk-container-wide pt-8 pb-12"
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.05, delayChildren: 0.08 } },
              }}
            >
              <ul className="space-y-2">
                {NAV_LINKS.map((l) => (
                  <motion.li
                    key={l.key}
                    variants={{
                      hidden: { opacity: 0, x: 16 },
                      show: { opacity: 1, x: 0 },
                    }}
                  >
                    <NavLink
                      to={l.to}
                      end={l.to === "/"}
                      data-testid={NAV[`mobileLink${l.key.charAt(0).toUpperCase() + l.key.slice(1)}`]}
                      onClick={() => setOpen(false)}
                      style={{
                        display: "block",
                        fontFamily: "var(--font-heading)",
                        fontSize: "clamp(36px, 9vw, 56px)",
                        lineHeight: 1.05,
                        color: "var(--rk-cream)",
                        textDecoration: "none",
                        padding: "14px 0",
                        borderBottom: "1px solid rgba(181,114,42,0.4)",
                      }}
                    >
                      {l.label}
                    </NavLink>
                  </motion.li>
                ))}
              </ul>
              <div className="mt-10">
                <Link
                  to="/advisory"
                  onClick={() => setOpen(false)}
                  className="rk-btn-primary"
                  style={{ width: "100%" }}
                >
                  Work with Rakia
                </Link>
              </div>
              <div className="mt-12 rk-caption" style={{ color: "rgba(250,247,242,0.6)" }}>
                {SITE.location}
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
